/**
 * useWorkstationFacts — what a list of legal apps needs to know about the workspace's Windows workstation
 * (docs/PLAN-windows-workstation-20261009.md, W2): the programs one can host here and the ones this person signed in
 * to there, from the platform's own answer (prest0nProgramWorkstation 'status').
 *
 * Read once a minute at most per workspace, and again when the workstation window says the person pressed done. Any
 * failure (not deployed, not a member, the lever off) is "nothing opens on a workstation": the rows stay exactly as
 * they were, a desktop program's "see how".
 */
import { useEffect, useState } from 'react';
import { askWorkstation, type WorkstationAnswer } from './programWorkstationClient';
import { PROGRAM_WORKSTATION_SIGNED_IN_EVENT } from './openProgramWorkstation';
import type { WorkstationFacts } from '../composio/panel/legalAppsSectionModel';

export const WORKSTATION_FACTS_TTL_MS = 60_000;

const cache = new Map<string, { atMs: number; facts: WorkstationFacts }>();
const inflight = new Map<string, Promise<WorkstationFacts | undefined>>();

/** PURE. The rows' facts from the platform's answer. */
export function workstationFactsOf(
  answer: (Pick<WorkstationAnswer, 'hosts' | 'signedIn'> & Partial<Pick<WorkstationAnswer, 'state' | 'names'>>) | null | undefined,
): WorkstationFacts | undefined {
  if (!answer || !answer.hosts.length) return undefined;
  return {
    hosts: new Set(answer.hosts),
    signedIn: new Set(answer.signedIn),
    ...(answer.state ? { state: answer.state } : {}),
    names: new Map(Object.entries(answer.names ?? {})),
  };
}

/** The /desktop page's door to the workspace's Windows workstation: the program it opens for, by name. */
export interface ProgramWorkstationEntry {
  readonly program: string;
  readonly programName: string;
}

/**
 * PURE. The /desktop page's entry to the workstation, or null. Only once the workspace has one (an owner or admin
 * sets it up from the program's row in Legal apps; the page never offers that), only for a program the platform
 * named, and never while a sign-in is owed: the page shows one directive at a time, and an owed sign-in comes first.
 */
export function programWorkstationEntryOf(facts: WorkstationFacts | undefined, signInOwed: boolean): ProgramWorkstationEntry | null {
  if (signInOwed || !facts || !facts.state || facts.state === 'none' || facts.state === 'off') return null;
  for (const program of facts.hosts) {
    const programName = facts.names?.get(program);
    if (programName) return { program, programName };
  }
  return null;
}

async function read(accountId: string, fresh: boolean): Promise<WorkstationFacts | undefined> {
  const held = cache.get(accountId);
  if (!fresh && held && Date.now() - held.atMs < WORKSTATION_FACTS_TTL_MS) return held.facts;
  const running = inflight.get(accountId);
  if (running && !fresh) return running;
  const request = askWorkstation('status', accountId)
    .then((answer) => {
      const facts = workstationFactsOf(answer);
      if (facts) cache.set(accountId, { atMs: Date.now(), facts });
      else cache.delete(accountId);
      return facts;
    })
    .catch(() => undefined)
    .finally(() => inflight.delete(accountId));
  inflight.set(accountId, request);
  return request;
}

export function useWorkstationFacts(accountId: string | null | undefined): WorkstationFacts | undefined {
  const [facts, setFacts] = useState<WorkstationFacts | undefined>(() => (accountId ? cache.get(accountId)?.facts : undefined));
  useEffect(() => {
    if (!accountId) {
      setFacts(undefined);
      return undefined;
    }
    let alive = true;
    void read(accountId, false).then((f) => {
      if (alive) setFacts(f);
    });
    const onSignedIn = (e: Event) => {
      const d = (e as CustomEvent).detail as { accountId?: unknown } | undefined;
      if (d && d.accountId !== accountId) return;
      void read(accountId, true).then((f) => {
        if (alive) setFacts(f);
      });
    };
    document.addEventListener(PROGRAM_WORKSTATION_SIGNED_IN_EVENT, onSignedIn);
    return () => {
      alive = false;
      document.removeEventListener(PROGRAM_WORKSTATION_SIGNED_IN_EVENT, onSignedIn);
    };
  }, [accountId]);
  return facts;
}

/** Tests only. */
export function resetWorkstationFactsForTests(): void {
  cache.clear();
  inflight.clear();
}
